import React, { useContext, useMemo } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import { graphql } from "@/gql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";

import Layout from "./components/Layout";
import MUIExerciseTabPanel from "./components/MUIExerciseTabPanel";

type PageParams = {
  clientId: string;
  date: string;
};

type CommentOnExercise = {
  type: "COMMENT_ON_EXERCISE";
  payload: { comment: string };
};

type UpdateExerciseComment = {
  type: "UPDATE_EXERCISE_COMMENT";
  payload: { commentId: string; body: string; date: string | undefined | null };
};

type DeleteExerciseComment = {
  type: "DELETE_EXERCISE_COMMENT";
  payload: { commentId: string };
};

type Action = CommentOnExercise | UpdateExerciseComment | DeleteExerciseComment;

type Dispatch = (action: Action) => Promise<void>;

const ActionsContext = React.createContext<Dispatch | null>(null);

const clientExercisesPageQuery = graphql(`
  query ClientDiaryExercises($clientId: ID, $doneOn: ISO8601Date) {
    exercises(clientId: $clientId, doneOn: $doneOn) {
      nodes {
        ...ExerciseDetails
      }
    }

    exerciseComments(clientId: $clientId, date: $doneOn) {
      nodes {
        ...ClientExerciseComment
      }
    }

    coachViewer {
      id
    }
  }
`);

const ClientExercisePage: React.FC = () => {
  const { clientId, date } = useParams<PageParams>();
  const snackbar = useGlobalSnackbar();

  const { data } = useQuery(clientExercisesPageQuery, {
    variables: { clientId: clientId, doneOn: date },
  });

  const exercises = useMemo(() => {
    return (data?.exercises.nodes ?? []).slice();
  }, [data?.exercises.nodes]);
  const comments = data?.exerciseComments.nodes ?? [];

  const handleCommentOnExercise = useHandleCommentOnExercise(
    clientId ?? "",
    date ?? new Date().toDateString()
  );
  const handleUpdateExerciseComment = useHandleUpdateExerciseComment();
  const handleDeleteExerciseComment = useHandleDeleteExerciseComment();

  const dispatch = async (action: Action) => {
    switch (action.type) {
      case "COMMENT_ON_EXERCISE":
        return handleCommentOnExercise(action.payload).catch((e) => {
          snackbar.open(
            displayError(
              e,
              "Something went wrong while adding your comment. Please try again later."
            )
          );
        });
      case "UPDATE_EXERCISE_COMMENT":
        return handleUpdateExerciseComment(action.payload).catch((e) => {
          snackbar.open(
            displayError(
              e,
              "Something went wrong while updating your comment. Please try again later."
            )
          );
        });
      case "DELETE_EXERCISE_COMMENT":
        return handleDeleteExerciseComment(action.payload)
          .then(() => {
            snackbar.open("Your comment has been deleted.");
          })
          .catch((e) => {
            snackbar.open(
              displayError(
                e,
                "Something went wrong while deleting your comment. Please try again later."
              )
            );
          });
    }
  };

  return (
    <ActionsContext.Provider value={dispatch}>
      <Layout
        data={exercises}
        comments={comments}
        coachId={data?.coachViewer.id}
        renderExercise={(exercise) => (
          <MUIExerciseTabPanel key={exercise.id} exercise={exercise} />
        )}
      />
    </ActionsContext.Provider>
  );
};

export function useActions(): Dispatch {
  const dispatch = useContext(ActionsContext);
  if (dispatch == null) throw new Error("No actions context provider found.");
  return dispatch;
}

const addExerciseCommentMutation = graphql(`
  mutation AddExerciseComment($input: CreateExerciseCommentInput!) {
    createExerciseComment(input: $input) {
      exerciseComment {
        id
        body
      }
    }
  }
`);

const afterAddExerciseCommentQuery = graphql(`
  query AfterAddExerciseComment($clientId: ID!, $date: ISO8601Date!) {
    exerciseComments(clientId: $clientId, date: $date) {
      nodes {
        ...ClientExerciseComment
      }
    }

    coachViewer {
      id
      activeClientsLimitReached
    }

    client(id: $clientId) {
      id
      status
    }
  }
`);

function useHandleCommentOnExercise(clientId: string, date: string) {
  const [commentOnExercise] = useMutation(addExerciseCommentMutation);
  const handler = async (payload: CommentOnExercise["payload"]) => {
    const { comment } = payload;
    await commentOnExercise({
      variables: {
        input: {
          attributes: { clientId: clientId, date: date, body: comment },
        },
      },
      refetchQueries: [
        { query: afterAddExerciseCommentQuery, variables: { clientId, date } },
      ],
      awaitRefetchQueries: true,
    });
  };
  return handler;
}

const updateExerciseMutation = graphql(`
  mutation UpdateExerciseComment($input: UpdateExerciseCommentInput!) {
    updateExerciseComment(input: $input) {
      exerciseComment {
        id
        body
      }
    }
  }
`);

function useHandleUpdateExerciseComment() {
  const [updateMealComment] = useMutation(updateExerciseMutation);
  const handler = async (payload: UpdateExerciseComment["payload"]) => {
    const { commentId, body, date } = payload;
    await updateMealComment({
      variables: {
        input: { id: commentId, patch: { body: body, date: date } },
      },
      optimisticResponse: {
        __typename: "Mutation",
        updateExerciseComment: {
          __typename: "UpdateExerciseCommentPayload",
          exerciseComment: {
            __typename: "ExerciseComment",
            id: commentId,
            body: body,
          },
        },
      },
    });
  };
  return handler;
}

const deleteExerciseCommentMutation = graphql(`
  mutation DeleteExerciseComment($input: DeleteExerciseCommentInput!) {
    deleteExerciseComment(input: $input) {
      exerciseComment {
        id
        body
      }
    }
  }
`);

function useHandleDeleteExerciseComment() {
  const [deleteMealComment] = useMutation(deleteExerciseCommentMutation);
  const handler = async (payload: DeleteExerciseComment["payload"]) => {
    const { commentId } = payload;
    await deleteMealComment({ variables: { input: { id: commentId } } });
  };
  return handler;
}

export default ClientExercisePage;
